/**
 * Ask Lilia's level, asked once (Olivia, 12 Oct, design-handoffs/2026-10-12-ask-lilia-level):
 * a card in the conversation, never a dialog; picking saves to the account; Skip (or simply
 * sending a message) means Simple. Afterwards a quiet "Answers: Simple ▾" under the composer
 * changes it from the next answer on.
 *
 * The question uses how people describe themselves (New to it…); everything after it uses
 * the help pages' names (Simple · Standard · For LaTeX users).
 */
import { useEffect, useId, useRef, useState } from "react";
import i18n from "i18next";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";
import type { Proficiency } from "@/lib/api";
import { ASK_LEVELS } from "@/hooks/useAskLevel";
import { cn } from "@/lib/cn";

const t = (key: string, opts?: Record<string, unknown>) => i18n.t(`editor:ask.level.${key}`, opts);

export function levelName(level: Proficiency): string {
  return t(`name.${level}`);
}

/** The one-time question. */
export function AskLevelCard({ onPick, onSkip }: { onPick: (l: Proficiency) => void; onSkip: () => void }) {
  const titleId = useId();
  const [focus, setFocus] = useState(0);
  const rows = useRef<(HTMLButtonElement | null)[]>([]);
  const move = (d: number) => {
    const next = (focus + d + ASK_LEVELS.length) % ASK_LEVELS.length;
    setFocus(next);
    rows.current[next]?.focus();
  };
  return (
    <section
      data-testid="ask-level-card"
      aria-labelledby={titleId}
      className="rounded-[12px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)] p-3.5"
    >
      <h3 id={titleId} className="text-[13.5px] font-semibold text-[var(--lilia-text)]">{t("title")}</h3>
      <p className="mt-0.5 text-[12px] text-[var(--lilia-text-secondary)]">{t("sub")}</p>
      <div role="radiogroup" aria-labelledby={titleId} className="mt-2.5 flex flex-col gap-1.5">
        {ASK_LEVELS.map((l, i) => (
          <button
            key={l}
            ref={(el) => {
              rows.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={false}
            tabIndex={i === focus ? 0 : -1}
            data-testid={`ask-level-option-${l === "beginner" ? "new" : l === "intermediate" ? "some" : "pro"}`}
            data-level={l}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); move(1); }
              if (e.key === "ArrowUp" || e.key === "ArrowLeft") { e.preventDefault(); move(-1); }
            }}
            onClick={() => onPick(l)}
            className="flex min-h-[52px] flex-col justify-center rounded-[9px] border border-[var(--lilia-border-strong,var(--lilia-border))] px-3 py-2 text-left hover:bg-[var(--lilia-hover)] max-md:min-h-[60px]"
          >
            <span className="text-[13px] font-semibold text-[var(--lilia-text)]">{t(`opt.${l}`)}</span>
            <span className="text-[12px] text-[var(--lilia-text-secondary)]">{t(`mean.${l}`)}</span>
          </button>
        ))}
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2 text-[12px] text-[var(--lilia-text-muted)]">
        <span>{t("skipHint")}</span>
        <button type="button" data-testid="ask-level-skip" onClick={onSkip} className="rounded px-1.5 py-0.5 font-medium text-[var(--lilia-primary)] hover:bg-[var(--lilia-hover)]">
          {t("skip")}
        </button>
      </div>
    </section>
  );
}

/** "Got it, I'll keep things simple." — for 3 seconds after a pick. */
export function AskLevelAck({ level, onDone }: { level: Proficiency; onDone: () => void }) {
  useEffect(() => {
    const id = window.setTimeout(onDone, 3000);
    return () => window.clearTimeout(id);
  }, [onDone]);
  return (
    <p data-testid="ask-level-ack" role="status" className="text-[12px] text-[var(--lilia-text-muted)]">
      {t(`ack.${level}`)}
    </p>
  );
}

/** "Answers: Simple ▾" under the composer; a choice saves to the account. */
export function AskLevelControl({ level, notSet, onChange }: { level: Proficiency; notSet: boolean; onChange: (l: Proficiency) => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        data-testid="ask-level-control"
        data-level={notSet ? "" : level}
        disabled={notSet}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[12px] text-[var(--lilia-text-secondary)] hover:bg-[var(--lilia-hover)] disabled:cursor-default disabled:hover:bg-transparent"
      >
        {t("control", { level: notSet ? t("notSet") : levelName(level) })}
        {!notSet && <IconChevronDown size={12} />}
      </button>
      {open && (
        <div
          role="menu"
          data-testid="ask-level-menu"
          className="absolute bottom-full left-0 z-50 mb-1 w-[280px] rounded-[10px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)] p-1.5 shadow-[var(--lilia-shadow-menu,0_8px_24px_rgba(16,24,40,.14))]"
        >
          <div className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)]">{t("menuTitle")}</div>
          {ASK_LEVELS.map((l) => (
            <button
              key={l}
              type="button"
              role="menuitemradio"
              aria-checked={l === level}
              data-testid="ask-level-menu-item"
              data-level={l}
              onClick={() => {
                onChange(l);
                setOpen(false);
              }}
              className={cn("flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-[var(--lilia-hover)]")}
            >
              <span className="mt-0.5 w-3.5 shrink-0 text-[var(--lilia-primary)]">{l === level && <IconCheck size={14} />}</span>
              <span className="min-w-0">
                <span className="block text-[13px] font-medium text-[var(--lilia-text)]">{levelName(l)}</span>
                <span className="block text-[11.5px] text-[var(--lilia-text-secondary)]">{t(`mean.${l}`)}</span>
              </span>
            </button>
          ))}
          <div className="border-t border-[var(--lilia-border)] px-2 pb-0.5 pt-1.5 text-[11px] text-[var(--lilia-text-muted)]">{t("menuFoot")}</div>
        </div>
      )}
    </div>
  );
}
